import { useApolloClient, useQuery } from '@apollo/client'
import { SnackbarVariant, useSnackbar } from '@focaldata/cin-ui-components'
import { useCallback, useEffect, useRef, useState } from 'react'
import { GET_CUSTOM_CRITERIA } from '../../data/gql/questionnaire/queries/customCriteria'
import { GET_COST_QUOTE } from '../../data/gql/questionnaire/queries/getCostQuote'
import { getFeasibilityEstimateRefetchQuery } from '../../data/gql/questionnaire/queries/getFeasibilityEstimate'
import { GET_PRESET_AUDIENCES_V2 } from '../../data/gql/questionnaire/queries/getPresetAudiences'
import { LoggerErrorType } from '../../data/logger'
import { FieldworkAudienceType } from '../../data/model/audience'
import { FieldworkAudienceType as FwAudienceType } from '../../data/model/fieldwork'
import { useFieldworkData } from '../../modules/Audience/Audience.hooks'
import { captureApolloError } from '../../utils/HelperFunctions'
import { convertAudienceType } from '../../utils/audienceHelper'
import useDisplayedLOI from '../localState/useDisplayedLOI'
import { useProjectId } from '../useProjectId'
import { useSurveyId } from '../useSurveyId'
import { useUpdateFieldworkV2 } from './createOrUpdateFieldworkV2'
import useGetDraftAudience from './useGetDraftAudience'

export const CUSTOM_AUDIENCE_PRESET_NAME = 'Custom'

export interface AudiencePreset {
  name: string
  presetAudienceId?: string
  isWeighted?: boolean
  // Locale language the preset was defined for. Set when the preset came from
  // a multi-language fetch (useGetPresetAudiencesForLanguages).
  language?: string
}

/**
 * All the languages a market fields, primary locale first. The order matters:
 * mergeLocalizedPresets keeps the FIRST list's "Custom" entry, so the primary
 * language must lead. Shared by every preset surface that derives its
 * language set from a fieldwork (picker container, weighted-preset lock,
 * language allocations) so they can't drift.
 */
export const getMarketPresetLanguages = (
  fieldwork:
    | {
        locale: { language: string }
        languageAllocations?: { language: string }[] | null
      }
    | null
    | undefined
): string[] => {
  const primary = fieldwork?.locale.language
  if (!primary) return []
  const allocated = (fieldwork.languageAllocations ?? []).map(
    ({ language }) => language
  )
  return [primary, ...allocated.filter((language) => language !== primary)]
}

/**
 * Merges per-language preset lists (in the order fetched — primary language
 * first) into one list for a market that fields several languages:
 * - The backend returns a "Custom" preset per locale; only the primary
 *   language's one is kept — "custom" is locale-agnostic in the UI.
 * - A name that appears in more than one language gets a ` (XX)` language
 *   suffix so the options stay distinguishable; unique names are left alone.
 */
export const mergeLocalizedPresets = (
  presetsByLanguage: AudiencePreset[][]
): AudiencePreset[] => {
  const merged: AudiencePreset[] = []
  let customSeen = false
  for (const presets of presetsByLanguage) {
    for (const preset of presets) {
      if (preset.name === CUSTOM_AUDIENCE_PRESET_NAME) {
        if (customSeen) continue
        customSeen = true
      }
      merged.push(preset)
    }
  }

  const nameCounts = new Map<string, number>()
  merged.forEach(({ name }) =>
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1)
  )
  return merged.map((preset) =>
    (nameCounts.get(preset.name) ?? 0) > 1 && preset.language
      ? { ...preset, name: `${preset.name} (${preset.language.toUpperCase()})` }
      : preset
  )
}

/**
 * Preset audiences across all the languages a market fields (e.g. Belgium
 * nl + fr shows the presets of both locales). Pass the primary language first
 * — its "Custom" preset is the one kept. Queries are cache-first (`getPreset
 * AudiencesV2` is static config data), so re-runs after a language is added
 * or removed only hit the network for locales not seen before.
 */
export const useGetPresetAudiencesForLanguages = (
  countryCode: string,
  languages: string[]
) => {
  const client = useApolloClient()
  // Callers rebuild the array every render; key on the contents so the
  // effect only re-runs when the language set actually changes.
  const languagesKey = languages.join(',')

  // Synchronous cache read of the full merged result — non-null only when
  // EVERY locale's list is already cached. Used to seed the initial state and
  // to short-circuit the effect, so a remount with a warm cache renders the
  // presets on the first paint instead of flashing the loading state (the old
  // single-locale useQuery was cache-first-synchronous; this keeps parity).
  const readMergedFromCache = useCallback((): AudiencePreset[] | null => {
    const languageList = languagesKey ? languagesKey.split(',') : []
    if (!countryCode || languageList.length === 0) return []
    const lists: AudiencePreset[][] = []
    for (const language of languageList) {
      const cached = client.readQuery({
        query: GET_PRESET_AUDIENCES_V2,
        variables: { locale: { country: countryCode, language } }
      })
      if (!cached?.getPresetAudiencesV2) return null
      lists.push(
        cached.getPresetAudiencesV2.map((preset) => ({ ...preset, language }))
      )
    }
    return mergeLocalizedPresets(lists)
  }, [client, countryCode, languagesKey])

  // Which country+languages key the useState initializer already resolved
  // synchronously — lets the mount effect skip a second cache read and the
  // redundant setState/re-render that would come with it.
  const seededKeyRef = useRef<string | null>(null)
  const [state, setState] = useState<{
    data: AudiencePreset[]
    loading: boolean
  }>(() => {
    const cached = readMergedFromCache()
    if (cached) {
      seededKeyRef.current = `${countryCode}|${languagesKey}`
      return { data: cached, loading: false }
    }
    return { data: [], loading: true }
  })

  useEffect(() => {
    if (seededKeyRef.current === `${countryCode}|${languagesKey}`) {
      // Already seeded synchronously by the initializer for this exact key.
      // Consume the marker so later key changes re-run normally.
      seededKeyRef.current = null
      return undefined
    }
    const cached = readMergedFromCache()
    if (cached) {
      setState({ data: cached, loading: false })
      return undefined
    }
    const languageList = languagesKey ? languagesKey.split(',') : []
    if (!countryCode || languageList.length === 0) {
      setState({ data: [], loading: false })
      return undefined
    }
    let cancelled = false
    setState((current) => ({ ...current, loading: true }))
    Promise.all(
      languageList.map((language) =>
        client
          .query({
            query: GET_PRESET_AUDIENCES_V2,
            variables: { locale: { country: countryCode, language } },
            context: { clientName: 'questionnaire' },
            fetchPolicy: 'cache-first'
          })
          .then(
            (result) =>
              result.data.getPresetAudiencesV2?.map((preset) => ({
                ...preset,
                language
              })) ?? []
          )
          .catch((error) => {
            captureApolloError(
              LoggerErrorType.ApolloQuery,
              'getPresetAudiencesV2',
              error
            )
            // One locale failing shouldn't blank the whole dropdown.
            return [] as AudiencePreset[]
          })
      )
    ).then((lists) => {
      if (!cancelled) {
        setState({ data: mergeLocalizedPresets(lists), loading: false })
      }
    })
    return () => {
      cancelled = true
    }
  }, [client, countryCode, languagesKey, readMergedFromCache])

  return state
}

export const useSetPresetAudience = () => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const { displayedLOI } = useDisplayedLOI()
  const [loading, setLoading] = useState<boolean>(false)

  const { draftAudience, refetchAudience } = useGetDraftAudience()
  const { updateFieldworkV2 } = useUpdateFieldworkV2()
  const { fieldworkData } = useFieldworkData()
  const { enqueueSnackbar } = useSnackbar()

  const setPresetAudience: (preset: AudiencePreset) => Promise<void> = async (
    preset
  ) => {
    setLoading(true)
    try {
      let inputIfCustomMembers = {}
      const audience = fieldworkData?.audience[0]

      if (
        draftAudience?.members.some(
          (audience) => audience.memberType !== FieldworkAudienceType.Standard
        )
      ) {
        inputIfCustomMembers = {
          incidenceRate: 100,
          audienceDescription:
            audience?.__typename !== 'CintAudience'
              ? audience?.description
              : undefined
        }
      }

      const result = await updateFieldworkV2({
        variables: {
          input: {
            surveyId,
            panelSupplierCode: convertAudienceType(
              audience?.__typename as FwAudienceType
            ),
            ...inputIfCustomMembers,
            presetAudienceId: preset.presetAudienceId
          }
        },
        refetchQueries: [
          getFeasibilityEstimateRefetchQuery(projectId, surveyId, displayedLOI),
          GET_COST_QUOTE,
          // The preset swap replaces the audience members server-side, so the
          // screening criteria must be re-read wherever they are mounted.
          // Document-form: only active queries refetch. On the single-survey
          // page the criteria live in a dialog that remounts on open (no-op
          // here); on the multi-market audiences page they stay mounted behind
          // the Market settings drawer and would otherwise go stale.
          GET_CUSTOM_CRITERIA
        ]
      })
      // useUpdateFieldworkV2 registers onError, so failures resolve with no
      // data instead of rejecting. Surface them — otherwise the picker just
      // snaps back with zero feedback (e.g. when the backend rejects the
      // change because a non-base market's audience holds global questions).
      if (!result.data?.updateFieldworkV2) {
        throw new Error(
          result.errors?.[0]?.message ?? 'The audience could not be updated'
        )
      }

      await refetchAudience()
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : 'The audience could not be updated',
        { variant: SnackbarVariant.Error }
      )
    } finally {
      setLoading(false)
    }
  }

  return { setPresetAudience, loading }
}

export const useGetPresetAudiences = (
  countryCode: string,
  language: string
) => {
  const { data, loading } = useQuery(GET_PRESET_AUDIENCES_V2, {
    context: { clientName: 'questionnaire' },
    onError: (error) => {
      captureApolloError(
        LoggerErrorType.ApolloQuery,
        'getPresetAudiencesV2',
        error
      )
    },
    variables: {
      locale: {
        country: countryCode,
        language
      }
    },
    skip: !countryCode || !language
  })

  return { data: data?.getPresetAudiencesV2, loading }
}
